import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, MapPin, Route, Navigation } from "lucide-react";
import { getTranslatedValue } from "@/components/TranslatableField";
import { getUploadUrl } from "@/lib/utils";

/**
 * Unified ClubCard — used on / (ActiveClubsSection) and /clubes (ClubsPage).
 * Shows full description (no truncation).
 */
function ClubCardInner({ club, currentLang }) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  const clubName = getTranslatedValue(club.name, currentLang);
  const clubDescription = getTranslatedValue(club.description, currentLang);
  const imageUrl = (club.logo || club.photo)
    ? getUploadUrl(club.logo || club.photo)
    : null;
  const location = club.concelho || club.localidade;
  const region = club.distrito;
  const locationText = location && region
    ? `${location}, ${region}`
    : location || region || "Portugal";
  const dist = club.distance ?? club.distance_km;

  return (
    <Card
      className="bg-card border-border rounded-xl overflow-hidden group cursor-pointer hover:border-primary/50 transition-all h-full flex flex-col"
      onClick={() => navigate(`/clube/${club.club_id}`)}
      data-testid={`group-card-${club.club_id}`}
    >
      {/* Banner image */}
      <div className="relative">
        {imageUrl ? (
          <div className="h-40 bg-muted overflow-hidden">
            <img
              loading="lazy"
              src={imageUrl}
              alt={clubName}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        ) : (
          <div className="h-40 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <Users className="h-12 w-12 text-primary/30" />
          </div>
        )}

        {/* Distance badge */}
        {dist !== undefined && (
          <div className="absolute top-2 left-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
            <Navigation className="h-3 w-3" />
            {dist < 1
              ? `${Math.round(dist * 1000)} m`
              : `${Number(dist).toFixed(1)} km`}
          </div>
        )}

      </div>

      {/* Content */}
      <CardContent className="p-4 flex flex-col flex-1">
        {/* Avatar + name + location */}
        <div className="flex items-start gap-3 mb-2">
          <Avatar className="h-10 w-10 border-2 border-primary/20 flex-shrink-0">
            {imageUrl ? <AvatarImage src={imageUrl} alt={clubName} /> : null}
            <AvatarFallback className="bg-primary/10 text-primary font-bold">
              {clubName?.charAt(0)?.toUpperCase() || "G"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm truncate">{clubName}</h3>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{locationText}</span>
            </div>
          </div>
        </div>

        {/* Full description — no truncation */}
        {clubDescription && (
          <p className="text-sm text-muted-foreground mb-3">
            {clubDescription}
          </p>
        )}

        {/* Footer stats */}
        <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border/50 pt-3 mt-auto">
          <div className="flex items-center gap-1" title={t("clubs.members.title")}>
            <Users className="h-3.5 w-3.5" />
            <span>
              {club.member_count || 0}{" "}
              {(club.member_count || 0) === 1
                ? t("clubs.member")
                : t("clubs.members_plural")}
            </span>
          </div>
          {(club.trip_count || 0) > 0 && (
            <div className="flex items-center gap-1">
              <Route className="h-3.5 w-3.5" />
              <span>
                {club.trip_count || 0}{" "}
                {(club.trip_count || 0) === 1 ? t("clubs.trip") : t("clubs.trips")}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export const ClubCard = memo(ClubCardInner);
export default ClubCard;
